import { Lightbox, LightboxController, LightboxItem } from '@design-system';
import React, {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

type DynamicLightboxContextType = {
  lightbox?: LightboxController;
  addItem: (item: LightboxItem) => void;
  removeItem: (item: LightboxItem) => void;
};

type Props = {
  item: LightboxItem;
  children: (lightbox: LightboxController) => ReactElement<any, any> | null;
};

const DynamicLightboxContext = createContext<DynamicLightboxContextType>({
  addItem: () => {},
  removeItem: () => {},
});

export const DynamicLightbox: React.FC<Props> = ({ children, item }) => {
  const { addItem, lightbox, removeItem } = useContext(DynamicLightboxContext);

  useEffect(() => {
    addItem(item);
    return () => removeItem(item);
  }, []);

  if (lightbox === undefined) {
    console.warn(
      '<DynamicLightbox> is being used without a parent <DynamicLightboxProvider> component. This will not work.',
    );
  }

  return children(lightbox!);
};

export const DynamicLightboxProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [items, setItems] = useState<LightboxItem[]>([]);

  const addItem = useCallback((item: LightboxItem) => {
    setItems((current) => [...current, item]);
  }, []);

  const removeItem = useCallback((item: LightboxItem) => {
    setItems((current) => current.filter((i) => i.key !== item.key));
  }, []);

  return (
    <Lightbox items={items}>
      {(lightbox) => (
        <DynamicLightboxContext.Provider
          value={{ lightbox, addItem, removeItem }}
        >
          {children}
        </DynamicLightboxContext.Provider>
      )}
    </Lightbox>
  );
};
